/**
 * CENTRAL SUBKREWE LINK DEFINITIONS
 *
 * This is the single source of truth for the SubKrewe website / social media
 * links: which platforms exist, how they are labelled and iconified, how
 * captain input is normalized into an absolute URL, and how to build a list
 * for display.
 *
 * Import from this file rather than hard-coding platform lists elsewhere.
 */

/**
 * SubKrewe document fields that hold a link. Order is the order shown in
 * forms and in the UI.
 */
export const SUBKREWE_LINK_FIELDS = [
  'website',
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
  'twitter',
  'bluesky',
] as const

export type SubKreweLinkField = typeof SUBKREWE_LINK_FIELDS[number]

export interface SubKreweLinkPlatform {
  /** SubKrewe document field this platform is stored in */
  field: SubKreweLinkField
  label: string
  /** A PrimeIcons class ("pi pi-globe"), or an Iconify name for <Icon> (brand logos) */
  icon: string
  placeholder: string
  /**
   * Base URL used to expand a bare handle (e.g. "@mysubkrewe") into a profile
   * URL. Platforms without one (the website field) require a real URL.
   */
  handleBaseUrl?: string
}

export const SUBKREWE_LINK_PLATFORMS: readonly SubKreweLinkPlatform[] = [
  {
    field: 'website',
    label: 'Website',
    icon: 'pi pi-globe',
    placeholder: 'https://mysubkrewe.com',
  },
  {
    field: 'facebook',
    label: 'Facebook',
    icon: 'simple-icons:facebook',
    placeholder: 'https://facebook.com/mysubkrewe',
    handleBaseUrl: 'https://www.facebook.com/',
  },
  {
    field: 'instagram',
    label: 'Instagram',
    icon: 'simple-icons:instagram',
    placeholder: '@mysubkrewe',
    handleBaseUrl: 'https://www.instagram.com/',
  },
  {
    field: 'tiktok',
    label: 'TikTok',
    icon: 'simple-icons:tiktok',
    placeholder: '@mysubkrewe',
    handleBaseUrl: 'https://www.tiktok.com/@',
  },
  {
    field: 'youtube',
    label: 'YouTube',
    icon: 'simple-icons:youtube',
    placeholder: 'https://youtube.com/@mysubkrewe',
    handleBaseUrl: 'https://www.youtube.com/@',
  },
  {
    field: 'twitter',
    label: 'X (Twitter)',
    icon: 'simple-icons:x',
    placeholder: '@mysubkrewe',
    handleBaseUrl: 'https://x.com/',
  },
  {
    field: 'bluesky',
    label: 'Bluesky',
    icon: 'simple-icons:bluesky',
    placeholder: '@mysubkrewe.bsky.social',
    handleBaseUrl: 'https://bsky.app/profile/',
  },
] as const

const PLATFORMS_BY_FIELD: Record<string, SubKreweLinkPlatform> = SUBKREWE_LINK_PLATFORMS
  .reduce((acc: Record<string, SubKreweLinkPlatform>, platform) => {
    acc[platform.field] = platform
    return acc
  }, {})

export function isSubKreweLinkField(field: unknown): field is SubKreweLinkField {
  return typeof field === 'string' && field in PLATFORMS_BY_FIELD
}

export function getSubKreweLinkPlatform(field: unknown): SubKreweLinkPlatform | undefined {
  return typeof field === 'string' ? PLATFORMS_BY_FIELD[field] : undefined
}

/** Values that look like a link but must never end up in an href. */
const SCHEME_WITH_AUTHORITY = /^[a-z][a-z0-9+.-]*:\/\//i

/**
 * Normalize what a captain typed into an absolute http(s) URL.
 *
 * Accepts a full URL ("https://example.com/page"), a bare host
 * ("example.com/page"), or — for platforms with a `handleBaseUrl` — a handle
 * ("@mysubkrewe" or "mysubkrewe").
 *
 * @returns the normalized URL, `''` for empty input, or `null` when the value
 * cannot be turned into a safe http(s) URL (e.g. a `javascript:` scheme).
 */
export function normalizeSubKreweLink(field: unknown, value: unknown): string | null {
  if (value === null || value === undefined) return ''
  if (typeof value !== 'string') return null

  const raw = value.trim()
  if (!raw) return ''
  // Real URLs never contain raw whitespace; anything that does is a typo or
  // free text rather than a link.
  if (/\s/.test(raw)) return null

  const platform = getSubKreweLinkPlatform(field)
  const hasScheme = SCHEME_WITH_AUTHORITY.test(raw)

  // A schemeless value containing '@' is a handle, not a host — this also
  // keeps email addresses from being silently rewritten into profile URLs.
  if (!hasScheme && raw.includes('@')) {
    const handle = raw.replace(/^@+/, '')
    if (!platform?.handleBaseUrl || !handle || handle.includes('@')) return null
    return `${platform.handleBaseUrl}${handle}`
  }

  let url: URL
  try {
    url = new URL(hasScheme ? raw : `https://${raw}`)
  }
  catch {
    return null
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

  // A hostname without a dot isn't a domain. Typed without a scheme it's a
  // handle (e.g. "mysubkrewe" in the Instagram field); typed with one it's
  // simply broken.
  if (!url.hostname.includes('.')) {
    if (hasScheme || !platform?.handleBaseUrl) return null
    return `${platform.handleBaseUrl}${raw}`
  }

  // Keep bare origins tidy: "https://example.com" rather than the trailing
  // slash URL.toString() adds.
  if (url.pathname === '/' && !url.search && !url.hash) {
    return `${url.protocol}//${url.host}`
  }

  return url.toString()
}

/**
 * True when a value is acceptable for the given link field (empty included).
 * Used by the zod link rules in utils/subkreweLinkRule.ts.
 */
export function isValidSubKreweLink(field: unknown, value: unknown): boolean {
  return normalizeSubKreweLink(field, value) !== null
}

/**
 * Pull just the link fields out of an incoming payload, normalized.
 *
 * Fields absent from the input stay absent from the result, so this can be
 * spread into a partial update without resurrecting fields the caller never
 * touched. Values that can't be normalized are cleared rather than stored, so
 * nothing unsafe can reach an href.
 */
export function pickSubKreweLinkFields(
  data: Record<string, unknown> | null | undefined,
): Partial<Record<SubKreweLinkField, string>> {
  const result: Partial<Record<SubKreweLinkField, string>> = {}
  if (!data) return result

  for (const field of SUBKREWE_LINK_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(data, field)) continue
    result[field] = normalizeSubKreweLink(field, data[field]) ?? ''
  }

  return result
}

/** As `pickSubKreweLinkFields`, but returns the whole payload with the link fields normalized. */
export function normalizeSubKreweLinkFields<T extends Record<string, unknown>>(data: T): T {
  return { ...data, ...pickSubKreweLinkFields(data) }
}

export interface SubKreweLinkDisplay {
  field: SubKreweLinkField
  label: string
  icon: string
  /** Absolute, http(s)-only URL safe to use in an href */
  url: string
  /** Shorter, human-readable form of the URL */
  display: string
}

/** Source object for link helpers — anything carrying the link fields. */
export type SubKreweLinkSource = Partial<Record<SubKreweLinkField, unknown>> & {
  social_media?: unknown
}

/**
 * Strip the parts of a URL that add noise in a list: scheme, "www.", and any
 * trailing slash.
 */
function toDisplayText(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '')
}

/**
 * Build the list of links to render for a SubKrewe, in platform order.
 *
 * Falls back to the deprecated freeform `social_media` field (populated by the
 * 2025 CSV import) when no structured link is set, so legacy SubKrewes still
 * show something until their captain fills in the new fields.
 */
export function getSubKreweLinks(subkrewe: SubKreweLinkSource | null | undefined): SubKreweLinkDisplay[] {
  if (!subkrewe) return []

  const links: SubKreweLinkDisplay[] = []

  for (const platform of SUBKREWE_LINK_PLATFORMS) {
    const url = normalizeSubKreweLink(platform.field, subkrewe[platform.field])
    if (!url) continue
    links.push({
      field: platform.field,
      label: platform.label,
      icon: platform.icon,
      url,
      display: toDisplayText(url),
    })
  }

  if (links.length === 0) {
    const legacy = normalizeSubKreweLink('website', subkrewe.social_media)
    if (legacy) {
      links.push({
        field: 'website',
        label: 'Website',
        icon: 'pi pi-globe',
        url: legacy,
        display: toDisplayText(legacy),
      })
    }
  }

  return links
}

export function hasSubKreweLinks(subkrewe: SubKreweLinkSource | null | undefined): boolean {
  return getSubKreweLinks(subkrewe).length > 0
}
